import { useEffect } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import AppErrorFallback from "./components/AppErrorFallback.tsx";
import "./index.css";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn && typeof window !== "undefined") {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    // Noise filter: errors thrown by browser extensions / injected scripts.
    // These surface as `eval` frames with no app file and are not actionable.
    ignoreErrors: [
      "Cannot read properties of null (reading 'shadowRoot')",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],
    beforeSend(event) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (
        frames.length > 0 &&
        frames.every((f) => !f.filename || f.filename === "<anonymous>" || /^None$/.test(f.filename))
      ) {
        return null;
      }
      return event;
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

const container = document.getElementById("root")!;
const tree = (
  <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </Sentry.ErrorBoundary>
);

// If the container was prerendered (has element children), hydrate. Otherwise CSR-render.
// Note: `hasChildNodes()` returns true for comment nodes like the `<!--app-html-->`
// placeholder Vite serves in dev, which would trigger a bogus hydration.
if (container.firstElementChild) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}
